import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, Compass, Target } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { STATUS_COLORS } from '../utils/constants';
import CompareModal from '../components/CompareModal';
import './AnalyzePage.css';

// ── Job Analysis Panel ────────────────────────────────────────────────────────
const FIT_COLOR_MAP = { green: '#10B981', yellow: '#F59E0B', red: '#EF4444' };

const ScoreRing = ({ score, max = 10, color = '#2563EB', size = 80 }) => {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const pct = score / max;
  const dash = pct * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E2E6EE" strokeWidth="7" />
      <circle
        cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth="7"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      <text x={size/2} y={size/2 + 5} textAnchor="middle" fontSize="18" fontWeight="700" fontFamily="Fraunces, serif" fill="#111827">
        {score}
      </text>
    </svg>
  );
};

const QuestionsForm = ({ questions, onSubmit, loading }) => {
  const [answers, setAnswers] = useState({});

  const set = (id, val) => setAnswers(a => ({ ...a, [id]: val }));

  return (
    <div className="fit-questions">
      <div className="fit-questions-header">
        <div>
          <h3>Personalized Fit Assessment</h3>
          <p>Answer these questions for a deeper, tailored analysis of whether this role is right for you.</p>
        </div>
      </div>

      <div className="fit-q-list">
        {questions.map((q, i) => (
          <div key={q.id} className="fit-q-item">
            <div className="fit-q-num">{i + 1}</div>
            <div className="fit-q-body">
              <div className="fit-q-text">{q.question}</div>
              {q.purpose && <div className="fit-q-purpose">Why we ask: {q.purpose}</div>}

              {q.type === 'yesno' && (
                <div className="fit-q-yesno">
                  {['Yes', 'No', 'Not sure'].map(opt => (
                    <button
                      key={opt}
                      className={`fit-yesno-btn ${answers[q.id] === opt ? 'selected' : ''}`}
                      onClick={() => set(q.id, opt)}
                    >{opt}</button>
                  ))}
                </div>
              )}

              {q.type === 'mcq' && (
                <div className="fit-q-mcq">
                  {q.options?.map(opt => (
                    <button
                      key={opt}
                      className={`fit-mcq-btn ${answers[q.id] === opt ? 'selected' : ''}`}
                      onClick={() => set(q.id, opt)}
                    >{opt}</button>
                  ))}
                </div>
              )}

              {q.type === 'short' && (
                <textarea
                  className="fit-q-short"
                  placeholder="Your answer..."
                  value={answers[q.id] || ''}
                  onChange={e => set(q.id, e.target.value)}
                  rows={2}
                />
              )}

              {q.type === 'scale' && (
                <>
                  {/* Desktop: numbered dot buttons */}
                  <div className="fit-q-scale fit-q-scale-dots">
                    <span className="scale-label">{q.minLabel || 'Low'}</span>
                    <div className="scale-track">
                      {Array.from({ length: (q.max || 10) - (q.min || 1) + 1 }, (_, i) => (q.min || 1) + i).map(n => (
                        <button
                          key={n}
                          className={`scale-dot ${answers[q.id] === n ? 'selected' : ''}`}
                          onClick={() => set(q.id, n)}
                        >{n}</button>
                      ))}
                    </div>
                    <span className="scale-label">{q.maxLabel || 'High'}</span>
                  </div>
                  {/* Mobile: range slider */}
                  <div className="fit-q-scale-slider">
                    <div className="scale-slider-meta">
                      <span className="scale-slider-endpoint">{q.minLabel || 'Low'}</span>
                      <span className="scale-slider-val">{answers[q.id] ?? '–'}</span>
                      <span className="scale-slider-endpoint">{q.maxLabel || 'High'}</span>
                    </div>
                    <input
                      type="range"
                      className="scale-slider-input"
                      min={q.min || 1}
                      max={q.max || 10}
                      step={1}
                      value={answers[q.id] ?? Math.ceil(((q.max || 10) + (q.min || 1)) / 2)}
                      onChange={e => set(q.id, parseInt(e.target.value))}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        className="fit-submit-btn"
        onClick={() => onSubmit(answers)}
        disabled={loading}
      >
        {loading
          ? <><span className="btn-spinner" /> Analyzing your answers...</>
          : 'Get My Personalized Fit Score'
        }
      </button>
    </div>
  );
};

const FitResult = ({ result }) => {
  const scoreColor = FIT_COLOR_MAP[result.fitColor] || '#2563EB';
  return (
    <div className="fit-result">
      <div className="fit-result-header">
        <div>
          <div className="fit-result-label">Fit Score</div>
          <div className="fit-result-verdict">{result.fitVerdict}</div>
        </div>
        <ScoreRing score={result.fitScore} color={scoreColor} size={90} />
      </div>

      {result.greenLights?.length > 0 && (
        <div className="fit-signals fit-signals-good">
          <div className="fit-signals-title"><CheckCircle size={14} /> Green Lights</div>
          {result.greenLights.map((g, i) => <div key={i} className="fit-signal-item">{g}</div>)}
        </div>
      )}

      {result.dealbreakers?.length > 0 && (
        <div className="fit-signals fit-signals-bad">
          <div className="fit-signals-title"><AlertTriangle size={14} /> Watch Out</div>
          {result.dealbreakers.map((d, i) => <div key={i} className="fit-signal-item">{d}</div>)}
        </div>
      )}

      {result.personalInsights?.map((ins, i) => (
        <div key={i} className="analysis-section-card">
          <div className="asc-title">{ins.icon} {ins.title}</div>
          <div className="asc-content">{ins.content}</div>
        </div>
      ))}

      <div className="fit-recommendation">
        <div className="fit-rec-label"><Target size={14} /> Final Recommendation</div>
        <div className="fit-rec-text">{result.finalRecommendation}</div>
      </div>
    </div>
  );
};

const JobAnalysis = ({ jobs, onUpgrade }) => {
  const { user, refreshUser } = useAuth();
  const [selectedJobId, setSelectedJobId] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [fitResult, setFitResult] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingFit, setLoadingFit] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('select'); // 'select' | 'analysis' | 'questions' | 'fit'

  const exportAnalysisPDF = () => {
    if (!analysis || !selectedJob) return;
    const scoreColor = analysis.overallScore >= 7 ? '#10B981' : analysis.overallScore >= 4 ? '#F59E0B' : '#EF4444';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>ApplAI — Job Analysis: ${analysis ? selectedJob?.company : ''}</title>
<style>
  body{font-family:'Segoe UI',Arial,sans-serif;background:#F5F7FA;padding:32px;color:#111827;}
  .header{background:linear-gradient(135deg,#2563EB,#14B8A6);color:white;border-radius:12px;padding:24px 28px;margin-bottom:20px;}
  h1{margin:0;font-size:22px;}.sub{margin:4px 0 0;opacity:.85;font-size:13px;}
  .notice{background:#FFF3CD;border:1px solid #FDE68A;border-radius:8px;padding:10px 14px;font-size:12px;color:#92400E;margin-bottom:20px;}
  .hero{display:flex;align-items:center;gap:20px;background:white;border:1px solid #E2E6EE;border-radius:10px;padding:20px;margin-bottom:18px;}
  .score-ring{width:80px;height:80px;flex-shrink:0;}
  .headline{font-size:18px;font-weight:700;margin-bottom:8px;}
  .redflag{background:#FFF1F2;color:#BE123C;border:1px solid #FECDD3;border-radius:20px;padding:2px 10px;font-size:11px;display:inline-block;margin:2px;}
  .proscons{display:flex;gap:14px;margin-bottom:18px;}
  .pros{flex:1;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:10px;padding:14px;}
  .cons{flex:1;background:#FFFBEB;border:1px solid #FDE68A;border-radius:10px;padding:14px;}
  .pc-title{font-size:11px;font-weight:700;text-transform:uppercase;margin-bottom:8px;}
  .pc-item{font-size:13px;color:#374151;padding:4px 0;border-top:1px solid rgba(0,0,0,.06);}
  .section{background:white;border:1px solid #E2E6EE;border-radius:10px;padding:14px 16px;margin-bottom:10px;}
  .sec-title{font-size:13px;font-weight:700;margin-bottom:6px;}
  .sec-body{font-size:13px;color:#374151;line-height:1.6;}
  .advice{background:#EFF6FF;border:1.5px solid #BFDBFE;border-radius:10px;padding:16px 18px;margin-bottom:10px;}
  .advice-label{font-size:11px;font-weight:700;color:#2563EB;text-transform:uppercase;margin-bottom:6px;}
  .advice-text{font-size:13.5px;color:#1E3A8A;line-height:1.65;}
  .footer{margin-top:24px;text-align:center;font-size:11px;color:#9CA3AF;}
</style></head><body>
<div class="header"><h1>AI Job Analysis</h1><p class="sub">${selectedJob?.company} — ${selectedJob?.role} · ${new Date().toLocaleDateString('en-PH',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p></div>
<div class="notice">⚠️ This analysis is generated by AI and is not saved in ApplAI after leaving. This export helps you conserve your analysis credits.</div>
<div class="hero">
  <svg class="score-ring" viewBox="0 0 80 80"><circle cx="40" cy="40" r="32" fill="none" stroke="#E2E6EE" stroke-width="7"/><circle cx="40" cy="40" r="32" fill="none" stroke="${scoreColor}" stroke-width="7" stroke-dasharray="${(analysis.overallScore/10)*201} ${201-(analysis.overallScore/10)*201}" stroke-linecap="round" transform="rotate(-90 40 40)"/><text x="40" y="45" text-anchor="middle" font-size="18" font-weight="700" fill="#111827">${analysis.overallScore}</text></svg>
  <div><div style="font-size:11px;font-weight:700;color:#2563EB;text-transform:uppercase;margin-bottom:6px;">Overall Score / 10</div><div class="headline">${analysis.headline}</div>${analysis.redFlags?.length?`<div>${analysis.redFlags.map(f=>`<span class="redflag">🚩 ${f}</span>`).join('')}</div>`:''}</div>
</div>
<div class="proscons">
  <div class="pros"><div class="pc-title" style="color:#15803D">✅ Strengths</div>${analysis.pros?.map(p=>`<div class="pc-item">${p}</div>`).join('')}</div>
  <div class="cons"><div class="pc-title" style="color:#B45309">⚠️ Concerns</div>${analysis.cons?.map(c=>`<div class="pc-item">${c}</div>`).join('')}</div>
</div>
${analysis.sections?.map(s=>`<div class="section"><div class="sec-title">${s.icon} ${s.title}</div><div class="sec-body">${s.content}</div></div>`).join('')}
${analysis.advice?`<div class="advice"><div class="advice-label">🧭 What to do next</div><div class="advice-text">${analysis.advice}</div></div>`:''}
<div class="footer">ApplAI · AI-powered job application tracker</div>
</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const exportFitPDF = () => {
    if (!fitResult || !selectedJob) return;
    const scoreColor = fitResult.fitColor === 'green' ? '#10B981' : fitResult.fitColor === 'yellow' ? '#F59E0B' : '#EF4444';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>ApplAI — Fit Assessment: ${selectedJob?.company}</title>
<style>
  body{font-family:'Segoe UI',Arial,sans-serif;background:#F5F7FA;padding:32px;color:#111827;}
  .header{background:linear-gradient(135deg,#7C3AED,#2563EB);color:white;border-radius:12px;padding:24px 28px;margin-bottom:20px;}
  h1{margin:0;font-size:22px;}.sub{margin:4px 0 0;opacity:.85;font-size:13px;}
  .notice{background:#FFF3CD;border:1px solid #FDE68A;border-radius:8px;padding:10px 14px;font-size:12px;color:#92400E;margin-bottom:20px;}
  .verdict{background:white;border:1px solid #E2E6EE;border-radius:10px;padding:18px 20px;margin-bottom:18px;display:flex;align-items:center;gap:20px;}
  .score-ring{width:80px;height:80px;flex-shrink:0;}
  .verdict-text{font-size:17px;font-weight:700;}
  .signals{padding:14px 16px;border-radius:10px;margin-bottom:14px;}
  .good{background:#F0FDF4;border:1px solid #BBF7D0;}
  .bad{background:#FFFBEB;border:1px solid #FDE68A;}
  .sig-title{font-size:11px;font-weight:700;text-transform:uppercase;margin-bottom:8px;}
  .sig-item{font-size:13px;color:#374151;padding:4px 0;border-top:1px solid rgba(0,0,0,.06);}
  .insight{background:white;border:1px solid #E2E6EE;border-radius:10px;padding:13px 15px;margin-bottom:10px;}
  .ins-title{font-size:13px;font-weight:700;margin-bottom:5px;}
  .ins-body{font-size:13px;color:#374151;line-height:1.6;}
  .rec{background:#EFF6FF;border:1.5px solid #BFDBFE;border-radius:10px;padding:16px 18px;}
  .rec-label{font-size:11px;font-weight:700;color:#2563EB;text-transform:uppercase;margin-bottom:6px;}
  .rec-text{font-size:13.5px;color:#1E3A8A;line-height:1.65;}
  .footer{margin-top:24px;text-align:center;font-size:11px;color:#9CA3AF;}
</style></head><body>
<div class="header"><h1>Personalized Fit Assessment</h1><p class="sub">${selectedJob?.company} — ${selectedJob?.role} · ${new Date().toLocaleDateString('en-PH',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p></div>
<div class="notice">⚠️ This assessment is generated by AI and is not saved in ApplAI. Export it to conserve your assessment credits.</div>
<div class="verdict">
  <svg class="score-ring" viewBox="0 0 80 80"><circle cx="40" cy="40" r="32" fill="none" stroke="#E2E6EE" stroke-width="7"/><circle cx="40" cy="40" r="32" fill="none" stroke="${scoreColor}" stroke-width="7" stroke-dasharray="${(fitResult.fitScore/10)*201} ${201-(fitResult.fitScore/10)*201}" stroke-linecap="round" transform="rotate(-90 40 40)"/><text x="40" y="45" text-anchor="middle" font-size="18" font-weight="700" fill="#111827">${fitResult.fitScore}</text></svg>
  <div><div style="font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase;margin-bottom:6px;">Fit Score / 10</div><div class="verdict-text">${fitResult.fitVerdict}</div></div>
</div>
${fitResult.greenLights?.length?`<div class="signals good"><div class="sig-title" style="color:#15803D">✅ Green Lights</div>${fitResult.greenLights.map(g=>`<div class="sig-item">${g}</div>`).join('')}</div>`:''}
${fitResult.dealbreakers?.length?`<div class="signals bad"><div class="sig-title" style="color:#B45309">⚠️ Watch Out</div>${fitResult.dealbreakers.map(d=>`<div class="sig-item">${d}</div>`).join('')}</div>`:''}
${fitResult.personalInsights?.map(ins=>`<div class="insight"><div class="ins-title">${ins.icon} ${ins.title}</div><div class="ins-body">${ins.content}</div></div>`).join('')}
<div class="rec"><div class="rec-label">🧭 Final Recommendation</div><div class="rec-text">${fitResult.finalRecommendation}</div></div>
<div class="footer">ApplAI · AI-powered job application tracker</div>
</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const selectedJob = jobs.find(j => j._id === selectedJobId);

  const handleCreditError = (err) => {
    const data = err.response?.data;
    if (err.response?.status === 402 && data?.requiresUpgrade) {
      setError(`${data.message} Upgrade to Pro for more credits.`);
    } else if (err.response?.status === 402) {
      setError(data?.message || 'Not enough credits.');
    } else {
      setError(data?.message || 'Analysis failed. Check your GEMINI_API_KEY.');
    }
  };

  const runAnalysis = async () => {
    if (!selectedJobId) return;
    setLoadingAnalysis(true);
    setError('');
    setAnalysis(null);
    setQuestions(null);
    setFitResult(null);
    try {
      const res = await api.post(`/api/analyze/job/${selectedJobId}`);
      setAnalysis(res.data.analysis);
      setStep('analysis');
      refreshUser();
    } catch (err) {
      handleCreditError(err);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const loadQuestions = async () => {
    setLoadingQuestions(true);
    setError('');
    try {
      const res = await api.post(`/api/analyze/job/${selectedJobId}/questions`);
      setQuestions(res.data.questions);
      setStep('questions');
      refreshUser();
    } catch (err) {
      handleCreditError(err);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const submitAnswers = async (answers) => {
    setLoadingFit(true);
    setError('');
    try {
      const res = await api.post(`/api/analyze/job/${selectedJobId}/fit`, {
        answers,
        questions,
      });
      setFitResult(res.data.fitResult);
      setStep('fit');
      refreshUser();
    } catch (err) {
      handleCreditError(err);
    } finally {
      setLoadingFit(false);
    }
  };

  const reset = () => {
    setAnalysis(null); setQuestions(null); setFitResult(null);
    setStep('select'); setError('');
  };

  const scoreColor = analysis
    ? analysis.overallScore >= 7 ? '#10B981' : analysis.overallScore >= 4 ? '#F59E0B' : '#EF4444'
    : '#2563EB';

  return (
    <div className="analyze-job-panel">
      <div className="ajp-header">
        <div className="ajp-header-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        </div>
        <div>
          <h2 className="ajp-title">AI Job Analysis</h2>
          <p className="ajp-sub">Get a deep, personalized breakdown of any application</p>
        </div>
      </div>

      {/* Job selector */}
      <div className="ajp-selector">
        <label className="ajp-selector-label">Select a job application to analyze</label>
        <div className="ajp-selector-row">
          <select
            className="ajp-select"
            value={selectedJobId}
            onChange={e => { setSelectedJobId(e.target.value); reset(); }}
          >
            <option value="">— Choose an application —</option>
            {jobs.map(j => (
              <option key={j._id} value={j._id}>
                {j.company} — {j.role} [{j.status}]
              </option>
            ))}
          </select>

          {/* Always render both buttons — use visibility to avoid layout shift */}
          <button
            className="ajp-analyze-btn"
            onClick={runAnalysis}
            disabled={loadingAnalysis}
            style={{ visibility: (selectedJobId && step === 'select') ? 'visible' : 'hidden' }}
          >
            {loadingAnalysis
              ? <><span className="btn-spinner" /> Analyzing...</>
              : <>Analyze</>
            }
          </button>

          <button
            className="ajp-reset-btn"
            onClick={reset}
            style={{ visibility: step !== 'select' ? 'visible' : 'hidden' }}
          >
            ← New Analysis
          </button>
        </div>

        {/* Fixed-height preview strip — always occupies the same space */}
        <div className="ajp-preview-area">
          <div className="ajp-job-preview" style={{
            visibility: (selectedJob && step === 'select') ? 'visible' : 'hidden'
          }}>
            <div className="ajp-preview-avatar" style={{
              background: selectedJob ? STATUS_COLORS[selectedJob.status]?.bg : 'var(--bg-elevated)',
              color: selectedJob ? STATUS_COLORS[selectedJob.status]?.text : 'var(--text-muted)',
            }}>
              {selectedJob?.company?.[0] || '?'}
            </div>
            <div>
              <div className="ajp-preview-name">{selectedJob?.company} — {selectedJob?.role}</div>
              <div className="ajp-preview-meta">
                {selectedJob?.status}{selectedJob?.stage ? ` · ${selectedJob.stage}` : ''}{selectedJob?.workSetup !== 'Not specified' ? ` · ${selectedJob?.workSetup}` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>  {/* end ajp-selector */}

      {/* Credit info bar */}
      <div className="ajp-credit-bar">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
        <span>
          <strong>{user?.creditsRemaining ?? 15}</strong> credits remaining ·{' '}
          {step === 'select' && <>Analysis costs <strong>2 credits</strong></>}
          {step === 'analysis' && <>Fit Questions costs <strong>1 credit</strong></>}
          {step === 'questions' && <>Fit Analysis costs <strong>2 credits</strong></>}
          {step === 'fit' && <>Analysis complete</>}
        </span>
        {user?.tier !== 'pro' && (
          <button className="ajp-upgrade-link" onClick={onUpgrade}>Upgrade for 300 credits/mo →</button>
        )}
      </div>

      {error && <div className="ajp-error" style={{margin:'0 24px 0'}}>⚠️ {error}</div>}

      {/* Analysis result */}
      {analysis && step === 'analysis' && (
        <div className="analysis-result">
          {/* Export notice */}
          <div className="ar-export-notice">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            This analysis is not saved after leaving this page. Export to keep a record and conserve your credits.
            <button className="ar-export-pdf-btn" onClick={exportAnalysisPDF}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export PDF
            </button>
          </div>

          {/* Score + headline */}
          <div className="ar-hero">
            <div className="ar-score-wrap">
              <ScoreRing score={analysis.overallScore} color={scoreColor} size={96} />
              <div className="ar-score-label">Overall Score</div>
            </div>
            <div className="ar-headline-wrap">
              <div className="ar-job-tag">{selectedJob?.company} — {selectedJob?.role}</div>
              <div className="ar-headline">{analysis.headline}</div>
              {analysis.redFlags?.length > 0 && (
                <div className="ar-redflags">
                  {analysis.redFlags.map((f, i) => (
                    <span key={i} className="ar-redflag">🚩 {f}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pros & Cons */}
          <div className="ar-proscons">
            <div className="ar-pros">
              <div className="ar-pc-title"><CheckCircle size={13} /> Strengths</div>
              {analysis.pros?.map((p, i) => <div key={i} className="ar-pc-item">{p}</div>)}
            </div>
            <div className="ar-cons">
              <div className="ar-pc-title"><AlertTriangle size={13} /> Concerns</div>
              {analysis.cons?.map((c, i) => <div key={i} className="ar-pc-item">{c}</div>)}
            </div>
          </div>

          {/* Detailed sections */}
          <div className="ar-sections">
            {analysis.sections?.map((sec, i) => (
              <div key={i} className="analysis-section-card">
                <div className="asc-title">{sec.icon} {sec.title}</div>
                <div className="asc-content">{sec.content}</div>
              </div>
            ))}
          </div>

          {/* Advice */}
          {analysis.advice && (
            <div className="ar-advice">
              <div className="ar-advice-label"><Compass size={14} /> What to do next</div>
              <div className="ar-advice-text">{analysis.advice}</div>
            </div>
          )}

          {/* Fit assessment CTA */}
          {!questions && !fitResult && (
            <div className="ar-fit-cta">
              <div className="ar-fit-cta-left">
                <div className="ar-fit-cta-title">Want a deeper fit score?</div>
                <div className="ar-fit-cta-sub">
                  Answer {6}–{8} personalized questions and get a score tailored to your specific situation and priorities.
                </div>
              </div>
              <button className="ar-fit-cta-btn" onClick={loadQuestions} disabled={loadingQuestions}>
                {loadingQuestions
                  ? <><span className="btn-spinner" /> Generating...</>
                  : 'Take Fit Assessment →'
                }
              </button>
            </div>
          )}
        </div>
      )}

      {/* Questions */}
      {questions && step === 'questions' && (
        <QuestionsForm
          questions={questions}
          onSubmit={submitAnswers}
          loading={loadingFit}
        />
      )}

      {/* Fit result */}
      {fitResult && step === 'fit' && (
        <>
          <div className="ar-export-notice" style={{borderRadius:0}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Your fit assessment is not saved. Export it to keep a record and conserve your assessment credits.
            <button className="ar-export-pdf-btn" onClick={exportFitPDF}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export PDF
            </button>
          </div>
          <FitResult result={fitResult} />
        </>
      )}
    </div>
  );
};

// ── Main AnalyzePage ──────────────────────────────────────────────────────────
const AnalyzePage = ({ jobs, onUpgrade }) => {
  const [tab, setTab] = useState('analyze'); // 'analyze' | 'compare'

  return (
    <div className="analyze-root">
      {/* Page header */}
      <div className="analyze-page-header">
        <div>
          <h1 className="analyze-page-title">Analyze</h1>
          <p className="analyze-page-sub">AI-powered insights and side-by-side comparison for your applications</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="analyze-tabs">
        <button
          className={`atab ${tab === 'analyze' ? 'active' : ''}`}
          onClick={() => setTab('analyze')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          AI Job Analysis
        </button>
        <button
          className={`atab ${tab === 'compare' ? 'active' : ''}`}
          onClick={() => setTab('compare')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
          Compare Applications
        </button>
      </div>

      {/* Content */}
      <div key={tab} className="analyze-content analyze-content-enter">
        {tab === 'analyze' && <JobAnalysis jobs={jobs} onUpgrade={onUpgrade} />}
        {tab === 'compare' && (
          <div className="compare-tab-wrap">
            <CompareModal
              jobs={jobs}
              onClose={null}
              inline={true}
              onUpgrade={onUpgrade}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyzePage;
